/**
 * CreateEdgeCommand - Thin command for creating edge (transition) with undo/redo support
 *
 * Architecture:
 * - Command is THIN - only stores state for undo/redo
 * - ALL business logic is in FlowCanvasCoordinator
 * - Coordinator handles: validation, ScreenTransitionManager, Canvas updates, Firebase sync
 *
 * Execution flow:
 * 1. Command calls coordinator.createTransition()
 * 2. Coordinator does everything (struct update, Canvas, Firebase)
 * 3. Command stores created transition ID for undo
 *
 * Undo flow:
 * 1. Command calls coordinator.deleteTransition()
 * 2. Coordinator does everything (struct update, Canvas, Firebase)
 *
 * Sprint: sprint-2
 * Phase: 5 (Edge Commands Refactor - Thin Pattern)
 */

import type { CommandResult } from './Command';
import type { CommandContext } from './CommandContext';

/**
 * Command: Create new edge (transition) between screens
 *
 * This is a THIN command - all logic is delegated to Coordinator
 */
export class CreateEdgeCommand {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  private readonly sourceEntityId: string;
  private readonly targetEntityId: string;
  private readonly sourceActionKey: string;
  private readonly context: CommandContext;

  // Created transition ID (populated after execution, used for undo)
  private createdTransitionId: string | null = null;

  constructor(
    sourceEntityId: string,
    targetEntityId: string,
    sourceActionKey: string,
    context: CommandContext
  ) {
    this.id = `create-edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.sourceEntityId = sourceEntityId;
    this.targetEntityId = targetEntityId;
    this.sourceActionKey = sourceActionKey;
    this.context = context;
    this.timestamp = Date.now();
    this.description = `Create edge: ${sourceEntityId.slice(0, 8)} → ${targetEntityId.slice(0, 8)}`;
  }

  public canExecute(): boolean {
    // Basic validation - detailed validation is in Coordinator
    return !!this.sourceEntityId && !!this.targetEntityId && !!this.sourceActionKey;
  }

  public canUndo(): boolean {
    // Can undo if transition was created
    return !!this.createdTransitionId;
  }

  public async execute(): Promise<CommandResult> {
    if (!this.canExecute()) {
      return {
        success: false,
        error: new Error('Cannot execute: missing required fields'),
        affectedEntities: {}
      };
    }

    try {
      console.log('[CreateEdgeCommand] execute:', {
        source: this.sourceEntityId.slice(0, 8),
        target: this.targetEntityId.slice(0, 8),
        actionKey: this.sourceActionKey
      });

      // Coordinator does ALL the work:
      // 1. Validates screens exist
      // 2. Creates transition object with deterministic ID
      // 3. Updates screen.struct via ScreenTransitionManager
      // 4. Updates ScreenRegistry
      // 5. Point update Canvas (addEdge)
      // 6. Persists to Firebase
      const transition = await this.context.coordinator.createTransition({
        sourceEntityId: this.sourceEntityId,
        targetEntityId: this.targetEntityId,
        sourceActionKey: this.sourceActionKey,
        label: '',
      });

      this.createdTransitionId = transition.id;
      console.log('[CreateEdgeCommand]   ✓ Created:', this.createdTransitionId);

      return {
        success: true,
        affectedEntities: {
          transitions: [transition.id]
        }
      };
    } catch (error) {
      console.error('[CreateEdgeCommand] ❌ Execution failed:', error);
      return {
        success: false,
        error: error as Error,
        affectedEntities: {}
      };
    }
  }

  public async undo(): Promise<CommandResult> {
    if (!this.createdTransitionId) {
      return {
        success: false,
        error: new Error('Cannot undo: transition was not created'),
        affectedEntities: {}
      };
    }

    try {
      console.log('[CreateEdgeCommand] undo:', this.createdTransitionId);

      // Coordinator does ALL the work:
      // 1. Finds owning screen
      // 2. Removes transition from screen.struct via ScreenTransitionManager
      // 3. Updates ScreenRegistry
      // 4. Point update Canvas (removeEdge)
      // 5. Persists to Firebase
      await this.context.coordinator.deleteTransitions([this.createdTransitionId]);

      const transitionId = this.createdTransitionId;
      this.createdTransitionId = null;

      console.log('[CreateEdgeCommand]   ✓ Undone');

      return {
        success: true,
        affectedEntities: {
          transitions: [transitionId]
        }
      };
    } catch (error) {
      console.error('[CreateEdgeCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error as Error,
        affectedEntities: {}
      };
    }
  }

  public async redo(): Promise<CommandResult> {
    // Re-execute creation
    // IMPORTANT: TransitionIdGenerator will generate THE SAME ID
    // because it's deterministic (source + actionKey + target)
    return this.execute();
  }
}
